import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import {
  businessTypesUrl, setupFacilityUrl, uploadLogoUrl, ASSETS,
  starterCatalogPreviewUrl, loadStarterCatalogUrl,
} from '../BaseURL';
import { useFacility } from '../context/FacilityContext';
import { swalError, toastSuccess } from '../utils/swal';
import '../styles/public.css';

const GH_REGIONS = [
  'Greater Accra', 'Ashanti', 'Western', 'Western North', 'Central', 'Eastern',
  'Volta', 'Oti', 'Northern', 'Savannah', 'North East', 'Upper East', 'Upper West',
  'Bono', 'Bono East', 'Ahafo',
];

const stepFade = {
  initial: { opacity: 0, x: 24 },
  animate: { opacity: 1, x: 0, transition: { duration: 0.3, ease: 'easeOut' } },
  exit:    { opacity: 0, x: -24, transition: { duration: 0.2, ease: 'easeIn' } },
};

const SetupPage = () => {
  const navigate = useNavigate();
  const { configured, refresh } = useFacility();
  const [step, setStep] = useState(0);
  const [types, setTypes] = useState([]);
  const [logoPath, setLogoPath] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [completed, setCompleted] = useState(null); // { userId, branchId } once setup is done
  const [importStarter, setImportStarter] = useState(true);
  const [importing, setImporting] = useState(false);

  const [form, setForm] = useState({
    business_type: '',
    name: '',
    tagline: '',
    email: '',
    phone: '',
    location: '',
    region: 'Greater Accra',
    digital_address: '',
    currency: 'GHS',
    head_office_code: 'HQ',
    head_office_name: '',
    admin_full_name: '',
    admin_username: '',
    admin_email: '',
    admin_phone: '',
    admin_password: '',
    admin_password_confirm: '',
  });

  useEffect(() => {
    if (configured && !completed) navigate('/login', { replace: true });
  }, [configured, completed, navigate]);

  useEffect(() => {
    axios.get(businessTypesUrl).then((res) => {
      if (res.data?.success) setTypes(res.data.results || []);
    }).catch(() => {});
  }, []);

  // Fetch industry preview whenever the user picks a business type
  useEffect(() => {
    if (!form.business_type) { setPreview(null); return; }
    setLoadingPreview(true);
    axios.get(starterCatalogPreviewUrl(form.business_type))
      .then((res) => setPreview(res.data?.success ? res.data : null))
      .catch(() => setPreview(null))
      .finally(() => setLoadingPreview(false));
  }, [form.business_type]);

  const set = (k) => (e) => setForm((prev) => ({ ...prev, [k]: e?.target ? e.target.value : e }));

  const stepLabels = ['Business', 'Facility', 'Admin', 'Finish'];

  const canNext = useMemo(() => {
    if (step === 0) return !!form.business_type;
    if (step === 1) return form.name.trim().length >= 2;
    if (step === 2) {
      return (
        form.admin_full_name.trim() &&
        form.admin_username.trim() &&
        form.admin_email.trim() &&
        form.admin_password.length >= 8 &&
        form.admin_password === form.admin_password_confirm
      );
    }
    return false;
  }, [step, form]);

  const onLogo = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError(null); setUploading(true);
    try {
      const fd = new FormData();
      fd.append('logo', file);
      const res = await axios.post(uploadLogoUrl, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      if (res.data?.success) { setLogoPath(res.data.path); toastSuccess('Logo uploaded'); }
      else { const msg = res.data?.message || 'Upload failed'; setError(msg); swalError('Logo upload failed', msg); }
    } catch (err) {
      const msg = err.response?.data?.message || 'Upload failed';
      setError(msg); swalError('Logo upload failed', msg);
    } finally {
      setUploading(false);
    }
  };

  const submit = async () => {
    setError(null); setSubmitting(true);
    try {
      const payload = { ...form, logo: logoPath };
      delete payload.admin_password_confirm;
      const res = await axios.post(setupFacilityUrl, payload);
      if (res.data?.success) {
        await refresh();
        const me = {
          id: res.data.userId,
          full_name: form.admin_full_name,
          username: form.admin_username,
          email: form.admin_email,
          role: 'super_admin',
          branch_id: res.data.branchId,
        };
        localStorage.setItem('user', JSON.stringify(me));
        setCompleted({ userId: res.data.userId, branchId: res.data.branchId });
        setStep(3);
        toastSuccess('Facility configured');
      } else {
        const msg = res.data?.message || 'Setup failed';
        setError(msg); swalError('Setup failed', msg);
      }
    } catch (err) {
      const msg = err.response?.data?.message || 'Setup failed';
      setError(msg); swalError('Setup failed', msg);
    } finally {
      setSubmitting(false);
    }
  };

  const finish = async () => {
    if (importStarter && preview?.items?.length) {
      setImporting(true);
      try {
        await axios.post(loadStarterCatalogUrl, { business_type: form.business_type });
      } catch (err) {
        // Non-fatal — let the user proceed; they can re-import from Settings later.
        console.error('starter import failed:', err);
      } finally {
        setImporting(false);
      }
    }
    navigate('/management', { replace: true });
  };

  const selectedType = types.find((t) => t.key === form.business_type);

  return (
    <div className="wizard">
      <div style={{ maxWidth: 960, margin: '0 auto 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" className="pub-brand">
          <span className="mark">P</span>
          <span className="name">POS Ghana</span>
        </Link>
        <Link to="/" style={{ color: 'var(--mute)', textDecoration: 'none', fontSize: 14 }}>← Back to website</Link>
      </div>

      <motion.div
        className="wizard-card"
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div className="wizard-head">
          <motion.div
            className="mark"
            animate={{ rotate: completed ? 0 : [0, 6, -6, 0] }}
            transition={completed ? { duration: 0 } : { repeat: Infinity, repeatDelay: 4, duration: 0.9 }}
          >
            {completed ? '✓' : '⚙'}
          </motion.div>
          <div className="titles">
            <h2>{completed ? 'You’re all set' : 'Set up your facility'}</h2>
            <p>{completed
              ? 'Finalise your starter catalog and step into the dashboard.'
              : 'A one-time setup. Takes about three minutes.'}</p>
          </div>
        </div>

        <div className="wizard-steps">
          {stepLabels.map((label, i) => (
            <div key={label} className={`wizard-step ${i === step ? 'active' : i < step ? 'done' : ''}`}>
              <motion.div
                className="dot"
                animate={i === step ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                {i < step ? '✓' : i + 1}
              </motion.div>
              <span>{label}</span>
            </div>
          ))}
        </div>

        <div className="wizard-body">
          {error && <div className="notice">{error}</div>}

          <AnimatePresence mode="wait">
            {step === 0 && (
              <motion.div key="step0" {...stepFade}>
                <p style={{ color: 'var(--mute)', marginTop: 0, marginBottom: 18 }}>
                  Pick the kind of business you run. We’ll tailor categories, units, sample products,
                  and the home screen to match.
                </p>
                <div className="wiz-grid">
                  {types.map((t) => (
                    <motion.div
                      key={t.key}
                      className={`wiz-type ${form.business_type === t.key ? 'selected' : ''}`}
                      onClick={() => set('business_type')(t.key)}
                      role="button"
                      tabIndex={0}
                      whileHover={{ y: -2 }}
                      whileTap={{ scale: 0.97 }}
                    >
                      <div className="emoji">{t.icon}</div>
                      <h4>{t.label}</h4>
                      <p>{t.tagline}</p>
                    </motion.div>
                  ))}
                </div>

                <AnimatePresence>
                  {selectedType && (
                    <motion.div
                      key="preview"
                      initial={{ opacity: 0, y: 12 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: 12 }}
                      transition={{ duration: 0.3 }}
                      className="wiz-preview"
                    >
                      <div className="wiz-preview-head">
                        <div>
                          <h3>{selectedType.icon} {selectedType.label}</h3>
                          <p>Here’s what your POS will look like out of the box — fully editable later.</p>
                        </div>
                        {loadingPreview && <span className="muted">loading…</span>}
                      </div>
                      <div className="wiz-preview-grid">
                        {(preview?.items || []).slice(0, 8).map((it) => (
                          <motion.div
                            key={it.name}
                            className="wiz-prev-tile"
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.25 }}
                          >
                            <img src={`${ASSETS}${it.image}`} alt={it.name} />
                            <div className="nm">{it.name}</div>
                            <div className="px">GH₵ {Number(it.selling_price).toFixed(2)}</div>
                          </motion.div>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            )}

            {step === 1 && (
              <motion.div key="step1" {...stepFade}>
                <div className="wiz-grid-2">
                  <div className="f-row">
                    <label className="f-label">Business name *</label>
                    <input className="f-input" value={form.name} onChange={set('name')} placeholder="e.g. Adom Supermarket" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Tagline</label>
                    <input className="f-input" value={form.tagline} onChange={set('tagline')} placeholder="Fresh & affordable, every day" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Contact email</label>
                    <input className="f-input" type="email" value={form.email} onChange={set('email')} placeholder="hello@adom.com" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Phone</label>
                    <input className="f-input" value={form.phone} onChange={set('phone')} placeholder="0244 000 000" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Region</label>
                    <select className="f-select" value={form.region} onChange={set('region')}>
                      {GH_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                  <div className="f-row">
                    <label className="f-label">Town / Location</label>
                    <input className="f-input" value={form.location} onChange={set('location')} placeholder="East Legon, Accra" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Digital address (Ghana Post)</label>
                    <input className="f-input" value={form.digital_address} onChange={set('digital_address')} placeholder="GA-XXX-XXXX" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Currency</label>
                    <select className="f-select" value={form.currency} onChange={set('currency')}>
                      <option value="GHS">GHS — Ghana Cedi</option>
                      <option value="USD">USD — US Dollar</option>
                    </select>
                  </div>
                  <div className="f-row">
                    <label className="f-label">Head office branch code</label>
                    <input className="f-input" value={form.head_office_code} onChange={set('head_office_code')} placeholder="HQ" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Head office name</label>
                    <input className="f-input" value={form.head_office_name} onChange={set('head_office_name')} placeholder="Adom — Head Office" />
                  </div>
                </div>
                <div className="f-row" style={{ marginTop: 4 }}>
                  <label className="f-label">Logo (optional)</label>
                  <div className="logo-dropzone">
                    <input id="logoInput" type="file" accept="image/*" onChange={onLogo} disabled={uploading} />
                    <label htmlFor="logoInput" className="logo-drop">
                      {logoPath ? (
                        <img src={`${ASSETS}${logoPath}`} alt="logo" />
                      ) : (
                        <>
                          <div className="ic">⬆</div>
                          <div className="lbl">{uploading ? 'Uploading…' : 'Click to upload a logo'}</div>
                          <div className="sub">PNG, JPG or SVG up to 5MB</div>
                        </>
                      )}
                    </label>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="step2" {...stepFade}>
                <p style={{ color: 'var(--mute)', marginTop: 0, marginBottom: 18 }}>
                  Create the super-administrator account. This person controls all branches and can create more users later.
                </p>
                <div className="wiz-grid-2">
                  <div className="f-row">
                    <label className="f-label">Full name *</label>
                    <input className="f-input" value={form.admin_full_name} onChange={set('admin_full_name')} />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Username *</label>
                    <input className="f-input" value={form.admin_username} onChange={set('admin_username')} placeholder="superadmin" />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Email *</label>
                    <input className="f-input" type="email" value={form.admin_email} onChange={set('admin_email')} />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Phone</label>
                    <input className="f-input" value={form.admin_phone} onChange={set('admin_phone')} />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Password (min 8) *</label>
                    <input className="f-input" type="password" value={form.admin_password} onChange={set('admin_password')} />
                  </div>
                  <div className="f-row">
                    <label className="f-label">Confirm password *</label>
                    <input className="f-input" type="password" value={form.admin_password_confirm} onChange={set('admin_password_confirm')} />
                  </div>
                </div>
                {form.admin_password && form.admin_password !== form.admin_password_confirm && (
                  <div className="notice">Passwords do not match.</div>
                )}
              </motion.div>
            )}

            {step === 3 && completed && (
              <motion.div key="step3" {...stepFade}>
                <div className="wiz-success">
                  <motion.div
                    className="tick"
                    initial={{ scale: 0, rotate: -30 }}
                    animate={{ scale: 1, rotate: 0 }}
                    transition={{ type: 'spring', stiffness: 220, damping: 14 }}
                  >
                    ✓
                  </motion.div>
                  <h3>Welcome aboard, {form.admin_full_name?.split(' ')[0] || 'friend'}</h3>
                  <p>Your facility <strong>{form.name}</strong> is configured as a <strong>{selectedType?.label}</strong>.</p>
                </div>

                {preview?.items?.length > 0 && (
                  <div className="wiz-starter">
                    <label className="starter-toggle">
                      <input
                        type="checkbox"
                        checked={importStarter}
                        onChange={(e) => setImportStarter(e.target.checked)}
                      />
                      <span>
                        <strong>Load a starter catalog</strong> — {preview.items.length} {selectedType?.label.toLowerCase()} products
                        with opening stock at your head office. You can edit or remove any of them later.
                      </span>
                    </label>
                    <div className="wiz-preview-grid compact">
                      {preview.items.slice(0, 6).map((it) => (
                        <div key={it.name} className="wiz-prev-tile">
                          <img src={`${ASSETS}${it.image}`} alt={it.name} />
                          <div className="nm">{it.name}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        <div className="wizard-foot">
          {step < 3 ? (
            <>
              <button
                className="btn-ghost"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                disabled={step === 0 || submitting}
              >
                ← Back
              </button>
              {step < stepLabels.length - 2 ? (
                <button className="btn-primary" onClick={() => setStep((s) => s + 1)} disabled={!canNext}>
                  Continue →
                </button>
              ) : (
                <button className="btn-primary" onClick={submit} disabled={!canNext || submitting}>
                  {submitting ? 'Setting up…' : 'Complete setup'}
                </button>
              )}
            </>
          ) : (
            <>
              <span style={{ color: 'var(--mute)', fontSize: 13 }}>You can revisit any of this from Settings.</span>
              <button className="btn-primary" onClick={finish} disabled={importing}>
                {importing ? 'Loading catalog…' : 'Enter the dashboard →'}
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default SetupPage;
